import { ChevronRightIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetCourse, useGetLessons, useUpdateCourse } from "src/api/lms/lms";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";

type Props = {};

export default function Course({}: Props) {
  const { courseId } = useParams();
  const { data: course } = useGetCourse(String(courseId));
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/lms`}>
            <div className="text-gray-800">{"LMS"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <Link to={`/lms/courses`}>
            <div className="text-gray-800">{"Courses"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{course?.name}</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto px-8 pt-24">
        <div className="mx-auto flex max-w-2xl flex-col space-y-2">
          <div className="text-2xl font-medium text-gray-800">
            {course?.name}
          </div>
          <div className="max-w-2xl text-base font-light text-gray-600">
            {course?.description}
          </div>
          <div className="pt-10 pb-20">
            {!!course?.lessons.length ? (
              <div>
                <div className="text-base font-medium text-gray-800">
                  Lessons
                </div>
                <div className="space-y-2 py-1 pb-6">
                  {course.lessons.map((lesson) => (
                    <div
                      key={lesson._id}
                      className="flex w-full cursor-pointer items-start justify-between rounded-md border p-4 shadow-sm hover:bg-gray-50"
                      onClick={() => {
                        navigate(`/lms/lessons/${lesson._id}`);
                      }}
                    >
                      <div>
                        <div className="text-lg font-medium">
                          {lesson.title}
                        </div>
                        <div className="text-sm text-gray-600">
                          <div>{lesson.description}</div>
                        </div>
                      </div>
                      <div>
                        <iframe className="h-24 w-40" src={lesson.videoUrl} />
                      </div>
                    </div>
                  ))}
                </div>
                <AddLessonButton />
              </div>
            ) : (
              // <div className="mx-auto w-full rounded-md border border-dashed border-gray-400 p-12">
              //   <div>No Lessons Added</div>
              //   <div className="text-sm text-gray-500">
              //     Add lessons to your course to get started.
              //   </div>
              // </div>
              // <div className="w-full border bg-gray-50/70 p-12 rounded-md shadow-sm">
              //   <div className="text-xl font-medium">Lessons</div>
              //   <div className="pt-2.5 font-light text-gray-700">
              //     You have not added any lessons to this course. Begin by adding
              //     your first one!
              //   </div>
              //   <div className="pt-6">
              //     <AddLessonButton />
              //   </div>
              // </div>
              <div className="mx-auto w-fit">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Lessons</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      You have not added any lessons to this course. Begin by
                      adding your first one!
                    </div>
                    <div className="pt-6">
                      <AddLessonButton />
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AddLessonButton() {
  const { courseId } = useParams();
  const { data: lessons } = useGetLessons();
  const { data: course } = useGetCourse(String(courseId));
  const [open, setOpen] = useState(false);
  const updateCourseMutation = useUpdateCourse();
  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={"secondary"}
          onClick={() => setOpen(true)}
          disabled={
            !lessons?.filter(
              (lesson) => !course?.lessons.find((l) => l._id === lesson._id)
            ).length
          }
        >
          {/* <PlusIcon className="mr-2 h-4 w-4" /> */}
          Add Lesson
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit max-w-sm p-0" align="start">
        <Command>
          <CommandInput placeholder="Search.." className="h-9" />
          <CommandList>
            <CommandEmpty>No members found.</CommandEmpty>
            <CommandGroup>
              {lessons
                ?.filter(
                  (lesson) => !course?.lessons.find((l) => l._id === lesson._id)
                )
                .map((lesson) => (
                  <CommandItem
                    key={lesson._id}
                    value={lesson.title}
                    onSelect={() => {
                      if (!!course) {
                        updateCourseMutation.mutate({
                          _id: course._id,
                          lessonIds: [
                            ...course?.lessons.map((l) => l._id),
                            lesson._id,
                          ],
                        });
                      }
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <span className="truncate">{lesson.title}</span>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
